/**
 * Developer Access — Nishtha staff API (2026-10-08, Task 9).
 *
 * Mirrors backend/src/modules/dev-access/dev-access.types.ts (DTOs) and
 * dev-access.routes.ts (paths, mounted at /api/v1/dev-access). Every call
 * unwraps the house envelope `{ success, data }`. Staff never receive share
 * values: nothing here has a `value` field except nothing — by construction.
 *
 * Step-up (A17): creating a share needs a fresh staff email code sent as the
 * `X-Step-Up-Code` header; the code comes from `POST /dev-access/step-up/code`.
 */
import { api } from '../../utils/api';

export type DevItemKey = 'gateway_key' | 'base_url' | 'api_list' | 'allowed_ips';
export type DevSystem = 'idfc_gateway_prod' | 'idfc_gateway_uat';
export type RequestStatus = 'WAITING' | 'APPROVED' | 'SENT_BACK' | 'CANCELLED';
export type ShareStatus = 'READY' | 'OPENED' | 'EXPIRED' | 'CANCELLED';

export interface RequestDTO {
  id: string; ref: string;
  partnerId: string; partnerName: string;
  developerUserId: string; developerName: string; developerEmail: string;
  system: DevSystem; apis: string[]; items: DevItemKey[]; ipAddresses: string[]; reason: string;
  status: RequestStatus; reviewNote: string | null; reviewedById: string | null; reviewedAt: string | null;
  createdAt: string; updatedAt: string;
}

export interface ShareDTO {
  id: string; ref: string; requestId: string | null;
  partnerId: string; partnerName: string;
  developerUserId: string; developerName: string; developerEmail: string;
  title: string; itemNames: string[]; note: string | null; hasGatewayKey: boolean;
  status: ShareStatus; expiresAt: string; openedAt: string | null; wipedAt: string | null;
  openedIp: string | null; openedUserAgent: string | null;
  createdById: string; createdAt: string; updatedAt: string;
}

export interface EventDTO {
  id: string; type: string; actorUserId: string | null; actorName: string | null;
  ip: string | null; userAgent: string | null; detail: Record<string, unknown>; createdAt: string;
}

export interface ShareDetailDTO extends ShareDTO { events: EventDTO[]; request: RequestDTO | null }

export interface DeveloperDTO { userId: string; name: string; email: string; partnerId: string; partnerName: string }

export interface SendCodeResult { sentTo: string; expiresAt: string; resendAfterSec: number }

export interface CreateShareInput {
  requestId?: string;
  partnerId: string;
  developerUserId: string;
  title: string;
  items: Array<{ name: string; value: string }>;
  issueGatewayKey?: { accountId: string; apis: string[]; ip: string | null };
  expiresInHours: 24 | 72 | 168;
  note?: string;
}

/** Static copy of the API catalogue (backend DEV_API_CATALOGUE + A17 `lspAllowed`). */
export interface CatalogueEntry { key: string; label: string; scope: string; path: string; lspAllowed: boolean }
export const API_CATALOGUE: readonly CatalogueEntry[] = [
  { key: 'balance', label: 'Account balance', scope: 'cbs-acctenq-accountBalance', path: '/acctenq/v2/prefetchAccount', lspAllowed: false },
  { key: 'statement', label: 'Account statement', scope: 'cbs-acctenq-accountStatement', path: '/acctenq/v3/getAccountStatement', lspAllowed: false },
  { key: 'beneficiary_validation', label: 'Beneficiary validation', scope: 'paymentenq-beneValidation', path: '/beneficiary-validation-service/v1/benevalidation', lspAllowed: true },
  { key: 'fund_transfer', label: 'Fund transfer', scope: 'paymenttxn-v1fundTransfer', path: '/payment-txn/v1/fund-transfer', lspAllowed: true },
  { key: 'transaction_status', label: 'Transaction status', scope: 'paymentenq-paymentTransactionStatus', path: '/payment-enq/v1/transaction-status', lspAllowed: true },
];
export const LSP_API_KEYS: ReadonlySet<string> = new Set(API_CATALOGUE.filter((a) => a.lspAllowed).map((a) => a.key));

async function unwrap<T>(promise: Promise<{ data: { data: T } }>): Promise<T> {
  const res = await promise;
  return res.data.data;
}

export const STEP_UP_HEADER = 'X-Step-Up-Code';
export const stepUpHeaders = (code: string) => ({ headers: { [STEP_UP_HEADER]: code } });

export const devAccessApi = {
  requests: (status?: RequestStatus) => unwrap<RequestDTO[]>(api.get('/dev-access/requests', { params: status ? { status } : {} })),
  sendBack: (id: string, note: string) => unwrap<RequestDTO>(api.post(`/dev-access/requests/${encodeURIComponent(id)}/send-back`, { note })),
  shares: (status?: ShareStatus) => unwrap<ShareDTO[]>(api.get('/dev-access/shares', { params: status ? { status } : {} })),
  share: (id: string) => unwrap<ShareDetailDTO>(api.get(`/dev-access/shares/${encodeURIComponent(id)}`)),
  /** A17 step-up: `code` is the fresh staff email code. */
  createShare: (input: CreateShareInput, code: string) => unwrap<ShareDTO>(api.post('/dev-access/shares', input, stepUpHeaders(code))),
  cancelShare: (id: string) => unwrap<ShareDTO>(api.post(`/dev-access/shares/${encodeURIComponent(id)}/cancel`)),
  developers: (partnerId?: string) => unwrap<DeveloperDTO[]>(api.get('/dev-access/developers', { params: partnerId ? { partnerId } : {} })),
  stepUpCode: () => unwrap<SendCodeResult>(api.post('/dev-access/step-up/code')),
};

/** Error code from a failed axios call, or null. */
export function errorCode(err: unknown): string | null {
  const e = err as { response?: { data?: { code?: string } } };
  return e?.response?.data?.code ?? null;
}
export function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error ?? fallback;
}
